import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { backend_url } from '../../App';

const Faq = () => {
  const [faqs, setFaqs] = useState([]);
  const [openIndex, setOpenIndex] = useState(null);

  useEffect(() => {
    fetchFaqs();
  }, []);

  const fetchFaqs = async () => {
    try {
      const response = await axios.get(`${backend_url}/api/faq`);
      setFaqs(response.data);
    } catch (error) {
      console.error('Error fetching FAQs:', error);
    }
  };

  const toggleFaq = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  const renderFaq = (faq, index) => {
    const isOpen = openIndex === index;
    return (
      <div
        key={faq._id}
        className={`rounded-2xl border overflow-hidden transition-all duration-300
          ${isOpen
            ? "border-[var(--brand-primary-400)] shadow-[0_4px_20px_rgba(124,58,237,0.15)]"
            : "border-[var(--brand-primary-100)] hover:border-[var(--brand-primary-300)]"
          } bg-white`}
      >
        <button
          className="w-full flex items-center justify-between gap-3 px-4 sm:px-5 py-3.5 max-md:py-4 text-left"
          onClick={() => toggleFaq(index)}
        >
          <div className="flex items-center gap-3 min-w-0">
            {faq.icon_image && (
              <div className="w-8 h-8 rounded-lg bg-[var(--brand-primary-50)] flex items-center justify-center flex-shrink-0 overflow-hidden">
                <img
                  crossOrigin="anonymous"
                  src={`${faq.icon_image}`}
                  alt="icon"
                  className="w-5 h-5 object-contain"
                />
              </div>
            )}
            <span className={`text-sm font-semibold transition-colors ${isOpen ? "text-[var(--brand-primary-700)]" : "text-gray-800"}`}>
              {faq.question}
            </span>
          </div>
          <div
            className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 transition-all duration-300
              ${isOpen ? "text-white rotate-45" : "text-[var(--brand-primary-500)] rotate-0"}`}
            style={isOpen ? { background: "linear-gradient(135deg, var(--brand-primary-600), var(--brand-secondary-500))" } : { background: "var(--brand-primary-50)" }}
          >
            <span className="text-lg font-bold leading-none">+</span>
          </div>
        </button>

        <div
          className={`transition-all duration-300 ease-in-out overflow-hidden
            ${isOpen ? "max-h-96 opacity-100" : "max-h-0 opacity-0"}`}
        >
          <div className="px-4 sm:px-5 pb-4 pt-1">
            <div className="h-px bg-[var(--brand-primary-100)] mb-3" />
            <p className="text-gray-600 text-sm leading-relaxed">{faq.answer}</p>
          </div>
        </div>
      </div>
    );
  };

  // Split into two independent columns (keeps original index for open-state).
  const indexed = faqs.map((faq, i) => ({ faq, i }));
  const leftCol = indexed.filter(({ i }) => i % 2 === 0);
  const rightCol = indexed.filter(({ i }) => i % 2 === 1);

  return (
    <div
      className="relative overflow-hidden px-4 sm:px-8 py-12 max-md:py-9"
      style={{ background: "linear-gradient(180deg, var(--brand-bg) 0%, var(--brand-surface) 100%)" }}
    >
      {/* Faint dot grid */}
      <div
        className="absolute inset-0 opacity-[0.03] pointer-events-none"
        style={{
          backgroundImage: "radial-gradient(circle, var(--brand-primary-600) 1px, transparent 1px)",
          backgroundSize: "24px 24px",
        }}
      />

      <div className="relative max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex flex-col items-center text-center mb-8 max-md:mb-6 gap-2">
          <span
            className="text-white text-xs font-bold px-4 py-1 rounded-full"
            style={{ background: "linear-gradient(90deg, var(--brand-primary-600), var(--brand-secondary-500))" }}
          >
            💡 Got Questions?
          </span>
          <h2
            className="text-2xl sm:text-3xl md:text-4xl font-semibold text-gray-900"
            style={{ fontFamily: "'Times New Roman', Serif" }}
          >
            Frequently Asked Questions
          </h2>
          <div className="flex items-center gap-3">
            <div className="h-px w-12 bg-gradient-to-r from-transparent to-[var(--brand-primary-400)]" />
            <div className="w-1.5 h-1.5 rotate-45 bg-[var(--brand-primary-500)]" />
            <div className="h-px w-12 bg-gradient-to-l from-transparent to-[var(--brand-primary-400)]" />
          </div>
          <p className="text-gray-500 text-sm max-w-xl mt-1">
            Everything you need to know about our fabrics, sarees, customization and orders.
          </p>
        </div>

        {faqs.length === 0 ? (
          <div className="text-center py-12 text-gray-400 rounded-2xl border border-dashed border-[var(--brand-primary-100)] bg-white">
            <p className="text-4xl mb-3">❓</p>
            <p>No FAQs available yet.</p>
          </div>
        ) : (
          <>
            {/* Two-column accordion grid (single column on mobile) */}
            <div className="grid md:grid-cols-2 gap-3 items-start">
              <div className="flex flex-col gap-3">{leftCol.map(({ faq, i }) => renderFaq(faq, i))}</div>
              <div className="flex flex-col gap-3">{rightCol.map(({ faq, i }) => renderFaq(faq, i))}</div>
            </div>

            {/* Slim contact bar */}
            <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3 rounded-2xl border border-[var(--brand-primary-100)] bg-white px-6 py-4 shadow-sm">
              <p className="text-gray-600 text-sm">Still have questions? We're here to help.</p>
              <a
                href="https://wa.me/919789389489?text=Hi%2C%20I%20have%20a%20question%20about%20your%20products"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-white text-sm font-semibold shadow hover:opacity-90 transition"
                style={{ background: "linear-gradient(90deg, #16a34a, #22c55e)" }}
              >
                <svg className="w-4 h-4 fill-white" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/>
                  <path d="M12 0C5.373 0 0 5.373 0 12c0 2.138.564 4.14 1.541 5.875L0 24l6.299-1.519A11.956 11.956 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 22c-1.86 0-3.607-.487-5.12-1.34L2 22l1.372-4.768A9.942 9.942 0 012 12C2 6.477 6.477 2 12 2s10 4.477 10 10-4.477 10-10 10z"/>
                </svg>
                Chat on WhatsApp
              </a>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Faq;
